import { useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import type { Client } from "@/briefpoint-client";
import { useClientApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { getClientsOptions } from "@/lib/query-options";
import { toLocaleUSDateString } from "@/lib/utils";
import { getCaseSourceLogo } from "@/utils/ExternalPartner/utils";
import Button from "../Button";
import Loading from "../Loading";
import Pagination from "../Pagination/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import styles from "./ClientsTable.module.scss";
import { ClientsTooltipWrapper } from "./ClientsTooltipWrapper";

interface Props {
  search: string;
  recent?: boolean;
}

export default function ClientsTable({ search, recent = false }: Props) {
  const clientApi = useClientApi();
  const { user } = useAuth();

  if (!user) throw new Error("User is undefined");

  const clientsQuery = useQuery({
    ...getClientsOptions(clientApi, user),
  });

  const filteredClients = useMemo(() => {
    let filtered = clientsQuery.data;
    if (recent) {
      filtered = filtered
        ?.slice()
        .sort((a, b) => {
          if (a.lastActivity && b.lastActivity) {
            return a.lastActivity > b.lastActivity ? -1 : 1;
          }
          if (a.lastActivity) {
            return -1;
          }
          if (b.lastActivity) {
            return 1;
          }
          return 0;
        })
        .slice(0, 5);
    }

    return filtered?.filter((x) =>
      x.name?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [clientsQuery.data, search, recent]);

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: "icon",
      header: "",
      meta: {
        width: "18px",
      },
      accessorFn: (row) => row.id ?? "",
      cell: ({ row }) => {
        return (
          <div className={styles.cellData}>
            {getCaseSourceLogo(row.original.integration?.partner)}
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Client Name
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "25%",
      },
      accessorFn: (row) => row.name?.toLowerCase() ?? "-",
      cell: ({ row }) => {
        return <ClientsTooltipWrapper name={row.original.name} />;
      },
    },
    {
      accessorKey: "respondentName",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Respondent Name
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "15%",
      },
      accessorFn: (row) => row.respondentName?.toLowerCase() ?? "-",
      cell: ({ row }) => {
        return <ClientsTooltipWrapper name={row.original.respondentName} />;
      },
    },
    {
      accessorKey: "contactFirstName",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            First Name
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "10%",
      },
      accessorFn: (row) => row.contactFirstName?.toLowerCase() ?? "-",
      cell: ({ row }) => {
        return <ClientsTooltipWrapper name={row.original.contactFirstName} />;
      },
    },
    {
      accessorKey: "contactLastName",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Last Name
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "10%",
      },
      accessorFn: (row) => row.contactLastName?.toLowerCase() ?? "-",
      cell: ({ row }) => {
        return <ClientsTooltipWrapper name={row.original.contactLastName} />;
      },
    },
    {
      accessorKey: "lastActivity",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Last Activity
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "10%",
      },
      sortingFn: (rowA, rowB) => {
        return (
          (rowA.original.lastActivity?.getTime() ?? 0) -
          (rowB.original.lastActivity?.getTime() ?? 0)
        );
      },
      cell: ({ row }) => {
        return (
          <div className={styles.cellData}>
            {row.original.lastActivity
              ? toLocaleUSDateString(row.original.lastActivity)
              : "-"}
          </div>
        );
      },
    },
    {
      accessorKey: "contactEmail",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Email
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "10%",
      },
      accessorFn: (row) => row.contactEmail?.trim().toLowerCase() ?? "-",
      cell: ({ row }) => {
        return (
          <ClientsTooltipWrapper name={row.original.contactEmail?.trim()} />
        );
      },
      sortingFn: "text",
    },

    {
      accessorKey: "aliases",
      header: ({ column }) => {
        return (
          <Button
            className={styles.fieldHeaderButton}
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Aliases
            {column.getIsSorted() === "asc" ? <ChevronUp /> : <ChevronDown />}
          </Button>
        );
      },
      meta: {
        width: "10%",
      },
      accessorFn: (row) => row.aliases?.toLowerCase() ?? "-",
      cell: ({ row }) => {
        return <ClientsTooltipWrapper name={row.original.aliases} />;
      },
    },
    {
      accessorKey: "actions",
      header: "",
      meta: {
        width: "10%",
      },
      cell: ({ row }) => {
        return (
          <div className={styles.cellData}>
            <Link
              className="btn btn-outline-primary btn-26"
              to={{
                pathname: `/clients/${row.original.id}`,
                state: {
                  client: row.original,
                },
              }}
            >
              View Client
            </Link>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredClients ?? [],
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    autoResetAll: false,
    initialState: {
      sorting: [{ id: recent ? "lastActivity" : "name", desc: recent }],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  return (
    <Loading isLoading={clientsQuery.isLoading}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className={styles.tableHeaderRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    style={{
                      width: (header.column.columnDef.meta as { width: string })
                        ?.width, // taken from column meta definition
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: (cell.column.columnDef.meta as { width: string })
                      ?.width, // taken from column meta definition
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="d-flex justify-content-end py-2">
        {table.getPageCount() > 1 && (
          <Pagination
            navigate={(page) => table.setPageIndex(page - 1)}
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            totalItemCount={filteredClients?.length ?? 0}
            itemsPerPage={table.getState().pagination.pageSize}
            showPageCount
          />
        )}
      </div>
    </Loading>
  );
}
